import React, { useState } from "react";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import Modal from "components/Layout/Modal";
import TextInput from "components/form/TextInput";
import Location from "components/form/Location";
import Button from "components/form/Button";
import Dropdown from "components/form/Dropdown";
import { getFormDataArrayObject, numberOnlyFromInput } from "utils/helpers";
import { dialCode, icons } from "utils/constants";
import UserDropdown from "components/form/UserDropdown";
import { handleUpdateCoAuthor, setPaperData, showSuccess, throwError } from "store/slices";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import usePermission from "hooks/usePermission";

const AddCoAuthorPopup = ({ onHide, paperData = {} }) => {
  const { co_author_details = [], authorEmail = "" } = paperData;
  const params = useParams();
  const { paperId, eventId } = params;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const { viewAccess, createAccess, updateAccess, deleteAccess } = usePermission('Event Management');
  const [inviteStatus, setInviteStatus] = useState({});

  const formattedCoAuthors = co_author_details?.map(item => ({
    id: item?.id || null,
    name: item?.name || "",
    email: item?.email_id || "",
    country: item?.country_id || "",
    phone: item?.phone || "",
    country_code: item?.country_code || "",
    co_author_status: item?.co_author_status || 0,
  })) || [];

  const primaryCoAuthor = formattedCoAuthors.find(ca => ca.co_author_status === 1)?.email || "";
  const secondaryCoAuthor = formattedCoAuthors.find(ca => ca.co_author_status === 2)?.email || "";

  
  const normalizedCoAuthors = Array.isArray(formattedCoAuthors)
  ? formattedCoAuthors.map((author) => ({
      ...author,
      email: "",
      phone: "",
    }))
  : [];


  
  const initialValues = {
    co_authors: normalizedCoAuthors.length > 0
      ? normalizedCoAuthors
      : [
        {
          id: null,
          name: "",
          email: "",
          country: "",
          phone: "",
          country_code: "",
          co_author_status: 0,
        },
      ],
    primary_co_author: primaryCoAuthor,
    secondary_co_author: secondaryCoAuthor,
  };

  const getUsedEmails = (values) => {
    const usedEmails = new Set();
    if (authorEmail) usedEmails.add(authorEmail.trim().toLowerCase());
    values?.co_authors?.forEach((c) => {
      if (c.email) usedEmails.add(c.email.trim().toLowerCase());
    });
    return usedEmails;
  };

  const isEmailUsed = (email, currentIndex, values) => {
    if (!email) return false;
    const emailLower = email.trim().toLowerCase();
    const usedEmails = getUsedEmails(values);
    const currentCoAuthorEmail =
      values?.co_authors?.[currentIndex]?.email?.trim().toLowerCase();
    if (currentCoAuthorEmail === emailLower) return false;
    return usedEmails.has(emailLower);
  };

  const validateCoAuthorEmail = (email, index, setFieldValue, values) => {
    const emailLower = email?.trim()?.toLowerCase();
    if (!emailLower) return false;

    const mainEmail = authorEmail?.trim()?.toLowerCase();
    if (emailLower === mainEmail) {
      dispatch(throwError({ message: "Main author cannot be co-author." }));
      setFieldValue(`co_authors[${index}].email`, "");
      return true;
    }

    if (isEmailUsed(emailLower, index, values)) {
      dispatch(
        throwError({ message: "This email is already used by another author." })
      );
      setFieldValue(`co_authors[${index}].email`, "");
      return true;
    }

    return false;
  };

  // Yup Validation Schema
  const validationSchema = Yup.object().shape({
    co_authors: Yup.array().of(
      Yup.object().shape({
        id: Yup.string().nullable(),
        name: Yup.string().required("Co-author name is required"),
        email: Yup.string()
          .email("Co-author email must be valid")
          .required("Co-author email is required"),
        country: Yup.string().required("Country is required"),
        country_code: Yup.string().required("Country code is required"),
        phone: Yup.string()
          .required("Co-author phone is required")
          .when("country_code", {
            is: (val) => val === "IN",
            then: (schema) =>
              schema
                .min(10, "Phone number must be 10 digits for India")
                .max(10, "Phone number must be 10 digits for India")
                .matches(/^[0-9]{10}$/, "Phone number must be 10 digits for India"),
            otherwise: (schema) =>
              schema
                .min(8, "Phone number must be at least 8 digits")
                .max(15, "Phone number cannot exceed 15 digits")
                .matches(/^[0-9]+$/, "Phone number must contain only numbers"),
          }),
        co_author_status: Yup.number().required(),
      })
    ),
    primary_co_author: Yup.string().when("co_authors", {
      is: (co_authors) => Array.isArray(co_authors) && co_authors.length >= 2,
      then: Yup.string()
        .required("Primary Co-author is required.")
        .test(
          "different-co-authors",
          "Primary & Secondary Co-authors cannot be the same. Please select different co-authors and review the note below.",
          function (value) {
            const { secondary_co_author } = this.parent;
            return !(value && secondary_co_author && value === secondary_co_author);
          }
        ),
      otherwise: Yup.string().nullable(),
    }),

    secondary_co_author: Yup.string().when("co_authors", {
      is: (co_authors) => Array.isArray(co_authors) && co_authors.length >= 2,
      then: Yup.string()
        .required("Secondary Co-author is required.")
        .test(
          "different-co-authors",
          "Primary & Secondary Co-authors cannot be the same. Please select different co-authors and review the note below.",
          function (value) {
            const { primary_co_author } = this.parent;
            return !(value && primary_co_author && value === primary_co_author);
          }
        ),
      otherwise: Yup.string().nullable(),
    }),
  });

  const handleSubmit = async (values) => {
    setIsSubmitting(true)
    try {
      const updatedCoAuthors = values?.co_authors.map((author) => {
        if (typeof author?.id === "number") {
          return {
            ...author,
            author_id: author?.id,
            id: "",
          };
        }
        return author;
      });
      const payload = {
        co_authors: updatedCoAuthors,
        abstract_id: paperId,
        event_id: eventId
      };
      const response = await dispatch(handleUpdateCoAuthor(getFormDataArrayObject(payload)));
      if (response?.data) {
        onHide()
        showSuccess(response?.message || 'Update Succesfully')
        dispatch(
          setPaperData({
            ...paperData,
            co_author_details: response?.data?.co_author_details || paperData?.co_author_details,
            primary_co_author: response?.data?.primary_co_author || paperData?.primary_co_author,
            secondary_co_author: response?.data?.secondary_co_author || paperData?.secondary_co_author,
          })
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <Modal
      titleBesideClose="Add Co-Authors"
      titleBesideCloseClassName={"text-18-300"}
      onHide={onHide}
      isTitleBorder
      isTitleVerticalCenter
    >
      <div className="cp-20">
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, handleChange, setFieldValue, errors }) => (
            <Form>
              <FieldArray
                name="co_authors"
                render={(arrayHelpers) => {
                  const addNewCoAuthor = () => {
                    arrayHelpers.push({
                      id: null,
                      name: "",
                      email: "",
                      country: "",
                      phone: "",
                      country_code: "",
                      co_author_status: 0,
                    });
                    setInviteStatus((prev) => ({
                      ...prev,
                      [values.co_authors.length]: false,
                    }));
                  };

                  const duplicateError =
                    values.co_authors.length >= 2 &&
                      values?.primary_co_author &&
                      values?.secondary_co_author &&
                      values?.primary_co_author === values?.secondary_co_author
                      ? "Primary & Secondary Co-authors cannot be the same. Please select different co-authors and review the note below."
                      : null;

                  return (
                    <>
                      {values.co_authors.map((coAuthor, index) => (
                        <div className="row align-items-start cmb-5" key={index}>
                          {/* EMAIL */}
                          <div className="col-md-6 col-12 cmb-24">
                            {inviteStatus[index] ? (
                              <TextInput
                                IsIcon
                                isRequired
                                icon={icons?.emailIcon}
                                label="Email ID"
                                id={`co_authors[${index}].email`}
                                value={coAuthor.email || ""}
                                onChange={(e) => {
                                  handleChange(e);
                                  validateCoAuthorEmail(
                                    e.target.value,
                                    index,
                                    setFieldValue,
                                    values
                                  );
                                }}
                                error={errors?.co_authors?.[index]?.email}
                                placeholder="Enter email"
                              />
                            ) : (
                              <UserDropdown
                                isRequired
                                showOnSearch
                                label="Email ID"
                                id={`co_authors[${index}].id`}
                                placeholder="Search co-author email"
                                value={coAuthor.id?.toString() || ""}
                                error={errors?.co_authors?.[index]?.email}
                                isShowEmail
                                existingList={values.co_authors.map((c) => ({
                                  id: c.id,
                                  name: c.name,
                                  email_id: c.email,
                                  country_id: c.country,
                                  mobile_number: c.phone,
                                  country_code: c.country_code,
                                }))}
                                filterID={values.co_authors
                                  .filter((_, i) => i !== index)
                                  .map((c) => c.id)
                                  .filter((id) => id)}
                                onChange={(e) => {
                                  if (e?.target?.data) {
                                    const {
                                      id,
                                      name,
                                      email_id,
                                      country_id,
                                      mobile_number,
                                      country_code,
                                    } = e.target.data;

                                    const emailLower = email_id?.toLowerCase();
                                    if (isEmailUsed(emailLower, index, values)) {
                                      dispatch(
                                        throwError({
                                          message:
                                            "This email is already used by main author or another co-author.",
                                        })
                                      );
                                      return;
                                    }

                                    setFieldValue(`co_authors[${index}].id`, id || "");
                                    setFieldValue(`co_authors[${index}].name`, name || "");
                                    setFieldValue(
                                      `co_authors[${index}].email`,
                                      email_id || ""
                                    );
                                    setFieldValue(
                                      `co_authors[${index}].country`,
                                      country_id ? parseInt(country_id) : ""
                                    );
                                    setFieldValue(
                                      `co_authors[${index}].phone`,
                                      mobile_number || ""
                                    );
                                    setFieldValue(
                                      `co_authors[${index}].country_code`,
                                      country_code || ""
                                    );
                                  }
                                }}
                                handelInvite={(e) => {
                                  setInviteStatus((prev) => ({
                                    ...prev,
                                    [index]: true,
                                  }));
                                  setFieldValue(
                                    `co_authors[${index}].email`,
                                    e?.value || ""
                                  );
                                }}
                              />
                            )}
                          </div>

                          {/* NAME */}
                          <div className="col-md-6 col-12 cmb-24">
                            <TextInput
                              isRequired
                              label={`Co-Author ${index + 1}`}
                              id={`co_authors[${index}].name`}
                              value={coAuthor.name}
                              onChange={handleChange}
                              placeholder="Enter name"
                            />
                          </div>

                          {/* COUNTRY */}
                          <div className="col-md-6 col-12 cmb-24">
                            <Location
                              isRequired
                              type="country"
                              data={{
                                label: "Country",
                                id: `co_authors[${index}].country`,
                                value: coAuthor?.country || "",
                                placeholder: "Select Country",
                                error: errors?.co_authors?.[index]?.country,
                                onChange: (e) => {
                                  handleChange(e);
                                  const selected = dialCode.find(
                                    (c) => c?.name === e.target?.data?.country
                                  );
                                  if (selected) {
                                    setFieldValue(
                                      `co_authors[${index}].country_code`,
                                      selected.code
                                    );
                                  } else {
                                    setFieldValue(
                                      `co_authors[${index}].country_code`,
                                      ""
                                    );
                                  }
                                },
                              }}
                            />
                          </div>

                          {/* PHONE */}
                          <div className="col-md-6 col-12 cmb-24">
                            <TextInput
                              isRequired
                              type="number"
                              isPhoneNumber
                              label="Contact"
                              placeholder="Phone Number"
                              id={`co_authors[${index}].phone`}
                              value={coAuthor?.phone || ""}
                              error={errors?.co_authors?.[index]?.phone}
                              phoneNumberData={{
                                id: `co_authors[${index}].country_code`,
                                value: coAuthor?.country_code || "",
                              }}
                              onChange={(e) => {
                                if (e.target.id.includes("phone")) {
                                  handleChange(numberOnlyFromInput(e));
                                } else {
                                  handleChange(e);
                                  handleChange({
                                    target: { id: `co_authors[${index}].phone`, value: "" },
                                  });
                                  setFieldValue(
                                    `co_authors[${index}].mobile_no_length`,
                                    e.target.length
                                  );
                                }
                              }}
                            />
                          </div>

                          {/* ACTION BUTTONS */}
                          <div className="d-flex justify-content-end gap-3">
                            {index === values.co_authors.length - 1 && (
                              <Button
                                text="+ Add Co-Author"
                                btnStyle="primary-outline"
                                className="h-35 text-14-500"
                                onClick={addNewCoAuthor}
                              />
                            )}
                            {values.co_authors.length > 1 && (
                              <Button
                                btnStyle="danger-outline"
                                icon={<i className="bi bi-trash" />}
                                className="cpt-5 cpb-5 cps-10 cpe-10 h-35"
                                onClick={() => {
                                  arrayHelpers.remove(index);
                                  setInviteStatus((prev) => {
                                    const copy = { ...prev };
                                    delete copy[index];
                                    return copy;
                                  });
                                }}
                              />
                            )}
                          </div>
                        </div>
                      ))}

                      {/* PRIMARY & SECONDARY */}
                      {values.co_authors.length >= 2 && (
                        <>
                          <div className="text-16-400-20 cmb-20">
                            Co-Authors (Primary & Secondary)
                          </div>
                          <div className="row">
                            <div className="col-md-6 cmb-24">
                              <Dropdown
                                label="Primary Co-Author*"
                                id="primary_co_author"
                                placeholder="Select Primary Co-Author"
                                options={values.co_authors}
                                optionKey="email"
                                optionValue="name"
                                onChange={(e) => {
                                  handleChange(e);
                                  const selectedId = e.target.value;
                                  values?.co_authors?.forEach((coAuthor, idx) => {
                                    if (coAuthor.email === selectedId) {
                                      setFieldValue(
                                        `co_authors[${idx}].co_author_status`,
                                        1
                                      );
                                    } else if (coAuthor.email === values.secondary_co_author) {
                                      setFieldValue(
                                        `co_authors[${idx}].co_author_status`,
                                        2
                                      );
                                    } else {
                                      setFieldValue(
                                        `co_authors[${idx}].co_author_status`,
                                        0
                                      );
                                    }
                                  });
                                }}
                                value={values.primary_co_author}
                                error={
                                  errors?.primary_co_author && !duplicateError
                                    ? errors.primary_co_author
                                    : ""
                                }
                              />
                            </div>
                            <div className="col-md-6 cmb-24">
                              <Dropdown
                                label="Secondary Co-Author*"
                                id="secondary_co_author"
                                placeholder="Select Secondary Co-Author"
                                options={values.co_authors}
                                optionKey="email"
                                optionValue="name"
                                onChange={(e) => {
                                  handleChange(e);
                                  const selectedId = e.target.value;
                                  values.co_authors.forEach((coAuthor, idx) => {
                                    if (coAuthor.email === values.primary_co_author) {
                                      setFieldValue(
                                        `co_authors[${idx}].co_author_status`,
                                        1
                                      );
                                    } else if (coAuthor.email === selectedId) {
                                      setFieldValue(
                                        `co_authors[${idx}].co_author_status`,
                                        2
                                      );
                                    } else {
                                      setFieldValue(
                                        `co_authors[${idx}].co_author_status`,
                                        0
                                      );
                                    }
                                  });
                                }}
                                value={values.secondary_co_author}
                                error={
                                  errors?.secondary_co_author && !duplicateError
                                    ? errors.secondary_co_author
                                    : ""
                                }
                              />
                            </div>

                            {duplicateError && (
                              <div className="text-danger text-14-400 cmb-10">{duplicateError}</div>
                            )}

                            <div>
                              <span>🎤 Note:</span>
                              <br />
                              <span className="text-14-500-24">
                                👤 Main Author –{" "}
                                <span className="text-14-300-24">
                                  Has the primary right to present.
                                </span>
                              </span>
                              <br />
                              <span className="text-14-500-24">
                                👩‍💻 Primary Co-author –{" "}
                                <span className="text-14-300-24">
                                  Can present along with the Main Author.
                                </span>
                              </span>
                              <br />
                              <span className="text-14-500-24">
                                👥 Secondary Co-author –{" "}
                                <span className="text-14-300-24">
                                  May assist, but can present only if the Main Author is unavailable.
                                </span>
                              </span>
                              <br />
                              <span className="text-14-500-24">
                                👫 Presentation Limit –{" "}
                                <span className="text-14-300-24">
                                  Only two persons are allowed to present physically (either one
                                  Author and one Co-author, or two Co-authors).
                                </span>
                              </span>
                            </div>
                          </div>
                        </>
                      )}

                      {/* CANCEL & SAVE BUTTON */}
                      <div className="d-flex justify-content-center cmt-20 gap-2">
                        <Button
                          text="Cancel"
                          btnStyle="light-outline"
                          className="cps-40 cpe-40 h-38"
                          onClick={onHide}
                          disabled={isSubmitting}
                        />
                        {updateAccess === "1" &&
                          <Button
                            text="Save"
                            btnStyle="primary-dark"
                            className="cps-40 cpe-40 h-38"
                            type="submit"
                            disabled={isSubmitting}
                            btnLoading={isSubmitting}
                          />}
                      </div>
                    </>
                  );
                }}
              />
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
};

export default AddCoAuthorPopup;
